import { compact } from '@art-suite/art-core-ts';
import { css } from '@emotion/react';
import { ApiOrgProduct } from '@kaiizen/kai-pro-api';
import { useAllProductSupplierOrgs, useCombinedProductSearch, useOrgProduct } from '@kaiizen/kai-pro-api-react-state';
import CloseIcon from '@mui/icons-material/Close';
import { useEffect, useMemo, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import {
  CombinedOrgProductRetailerProduct,
  getCombinedOrgProductFromCombinedSearchResponse,
  getProductSearchResultSupplierCell,
  getProductSupplierOrgIdForNewOrgProduct,
} from '../../Lib';
import { formatUsd } from '../../Presenters';
import { LAYOUT2, TYPOGRAPHY2 } from '../../Styles';
import { Button, LabeledInput } from '../../Widgets';

const COLUMN_WIDTHS = {
  isOrgProduct: 75,
  image: 100,
  price: 100,
  supplier: 100,
  title: 300,
  description: 500,
};
const COLUMN_DISPLAY_NAMES = {
  isOrgProduct: 'Added',
  image: 'Image',
  price: 'Price',
  supplier: 'Supplier',
  title: 'Title',
  description: 'Description',
};
const COLUMN_ORDER: (keyof typeof COLUMN_WIDTHS)[] = [
  'isOrgProduct',
  'image',
  'price',
  'supplier',
  'title',
  'description',
];

const MODAL_OVERLAY_STYLE = css`
  label: MODAL_OVERLAY_STYLE;
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: rgba(0, 0, 0, 0.5);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 2000;
`;

const MODAL_STYLE = css`
  label: MODAL_STYLE;
  background: var(--background);
  display: flex;
  flex-direction: column;
  box-shadow: 0 4px 20px rgba(0, 0, 0, 0.15);
  position: fixed;
  top: 100px;
  left: 100px;
  right: 100px;
  bottom: 100px;
  overflow: hidden;
`;

const MODAL_HEADER_STYLE = css`
  label: MODAL_HEADER_STYLE;
  ${TYPOGRAPHY2.largeBold}
  padding: ${LAYOUT2.spacing2}px;
  color: var(--text);
  border-bottom: 1px solid var(--border);
  flex-shrink: 0;
  display: flex;
  align-items: center;
  justify-content: space-between;
`;

const CLOSE_BUTTON_STYLE = css`
  label: CLOSE_BUTTON_STYLE;
  background: none;
  border: none;
  padding: ${LAYOUT2.spacing1}px;
  cursor: pointer;
  ${TYPOGRAPHY2.small}
  color: var(--text2);
  display: flex;
  align-items: center;
  justify-content: center;
  width: 32px;
  height: 32px;
`;

const SEARCH_CONTAINER_STYLE = css`
  label: SEARCH_CONTAINER_STYLE;
  padding: ${LAYOUT2.spacing2}px;
  flex-shrink: 0;
  display: flex;
  align-items: center;
  gap: ${LAYOUT2.spacing1}px;
`;

const RESULTS_CONTAINER_STYLE = css`
  label: RESULTS_CONTAINER_STYLE;
  flex: 1;
  overflow: auto;
  min-height: 0;
  height: 100%;
`;

const gridTemplateColumns = COLUMN_ORDER.map(column => COLUMN_WIDTHS[column] + 'px').join(' ');

const TABLE_HEADER_STYLE = css`
  label: TABLE_HEADER_STYLE;
  display: grid;
  grid-template-columns: ${gridTemplateColumns};
  position: sticky;
  top: 0;
  z-index: 10;
  background: var(--background2);
  border-bottom: 2px solid var(--border);
  flex-shrink: 0;
`;

const TABLE_HEADER_CELL_STYLE = css`
  label: TABLE_HEADER_CELL_STYLE;
  height: 40px;
  display: flex;
  align-items: center;
  padding: 0 ${LAYOUT2.spacing1p5}px;
  ${TYPOGRAPHY2.xSmallBold};
  color: var(--text1);
  border-right: 1px solid var(--border);
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
`;

const TABLE_ROW_STYLE = css`
  label: TABLE_ROW_STYLE;
  display: grid;
  grid-template-columns: ${gridTemplateColumns};
  min-height: 100px;
  border-bottom: 1px solid var(--background4);
  background: transparent;
  cursor: pointer;
  &:hover {
    background: var(--background2);
  }
`;

const TABLE_CELL_STYLE = css`
  label: TABLE_CELL_STYLE;
  padding: ${LAYOUT2.spacing1p5}px;
  position: relative;
  ${TYPOGRAPHY2.small};
  border-right: 1px solid var(--background4);
  display: flex;
  align-items: flex-start;
  overflow: hidden;
  text-overflow: ellipsis;
`;

const PRICE_CELL_STYLE = css`
  label: PRICE_CELL_STYLE;
  ${TABLE_CELL_STYLE};
  justify-content: right;
`;

const SUPPLIER_CELL_STYLE = css`
  label: SUPPLIER_CELL_STYLE;
  ${TABLE_CELL_STYLE};
  ${TYPOGRAPHY2.xSmall};
`;

const TITLE_CELL_STYLE = css`
  label: TITLE_CELL_STYLE;
  ${TABLE_CELL_STYLE};
  white-space: normal;
  word-wrap: break-word;
  overflow-wrap: break-word;
`;

const DESCRIPTION_CELL_STYLE = css`
  label: DESCRIPTION_CELL_STYLE;
  ${TABLE_CELL_STYLE};
  white-space: normal;
  word-wrap: break-word;
  overflow-wrap: break-word;
`;

const IMAGE_CELL_STYLE = css`
  label: IMAGE_CELL_STYLE;
  ${TABLE_CELL_STYLE};
  padding: 0;
  justify-content: center;
`;

const PRODUCT_IMAGE_STYLE = css`
  label: PRODUCT_IMAGE_STYLE;
  width: 100px;
  height: 100px;
  object-fit: cover;
  background: var(--background3);
`;

const NO_RESULTS_STYLE = css`
  label: NO_RESULTS_STYLE;
  text-align: center;
  padding: ${LAYOUT2.spacing4}px;
  color: var(--text2);
  ${TYPOGRAPHY2.medium}
  font-style: italic;
`;

export const CombinedProductSearchModal = (
  { isOpen, onClose, onAdd }: { isOpen: boolean; onClose: () => void; onAdd: (orgProduct: ApiOrgProduct) => void },
) => {
  const [searchInputValue, setSearchInputValue] = useState('');
  const [searchQuery, setSearchQuery] = useState('');
  const { productSupplierOrgs } = useAllProductSupplierOrgs();
  const { results: combinedSearchResults, isLoading } = useCombinedProductSearch(searchQuery);
  const { orgProductMutators } = useOrgProduct(undefined);
  const resultsContainerRef = useRef<HTMLDivElement>(null);

  const handleSearch = () => {
    setSearchQuery(searchInputValue);
  };

  useEffect(() => {
    if (resultsContainerRef.current) {
      resultsContainerRef.current.scrollTop = 0;
    }
  }, [searchQuery]);

  const displayedProducts = useMemo(() => {
    if (!searchQuery) return [];

    return combinedSearchResults.map(result => {
      const combinedProduct = getCombinedOrgProductFromCombinedSearchResponse(result);
      return combinedProduct;
    });
  }, [combinedSearchResults, searchQuery]);

  const handleAddProduct = async (orgProduct: CombinedOrgProductRetailerProduct) => {
    if (orgProduct.orgId && orgProduct.id) {
      onAdd(orgProduct as ApiOrgProduct);
    } else {
      const productSupplierOrgId = getProductSupplierOrgIdForNewOrgProduct(orgProduct, productSupplierOrgs?.homeDepot?.id);
      if (!productSupplierOrgId) return;
      // create org product first, under the supplier the result came from
      const newOrgProduct = await orgProductMutators.create({ productSupplierOrgId, productSupplierSku: orgProduct.sku });

      onAdd(newOrgProduct);
    }
    onClose();
  };

  if (!isOpen) return null;

  const modalContent = (
    <>
      <style>
        {`
          @keyframes spin {
            0% { transform: rotate(0deg); }
            100% { transform: rotate(360deg); }
          }
        `}
      </style>
      <div css={MODAL_OVERLAY_STYLE} onClick={onClose}>
        <div css={MODAL_STYLE} onClick={e => e.stopPropagation()}>
          <div css={MODAL_HEADER_STYLE}>
            <span>Search Organization Products</span>
            <button css={CLOSE_BUTTON_STYLE} onClick={onClose}>
              <CloseIcon />
            </button>
          </div>

          <div css={SEARCH_CONTAINER_STYLE}>
            <LabeledInput
              label=''
              value={searchInputValue}
              onEscape={onClose}
              placeholder='Search by title, description, or SKU...'
              autoFocus
              onChange={setSearchInputValue}
              onEnter={handleSearch}
            />
            <Button onClick={handleSearch} disabled={searchInputValue === searchQuery}>Search</Button>
          </div>

          <div ref={resultsContainerRef} css={RESULTS_CONTAINER_STYLE}>
            {(isLoading || displayedProducts.length === 0) && (
              <div css={NO_RESULTS_STYLE}>
                {isLoading
                  ? 'Loading organization products...'
                  : searchQuery
                  ? 'No products found matching your search'
                  : 'No organization products found'}
              </div>
            )}

            {!isLoading && displayedProducts.length > 0 && (
              <>
                {/* Table Header */}
                <div css={TABLE_HEADER_STYLE}>
                  {COLUMN_ORDER.map(column => <div css={TABLE_HEADER_CELL_STYLE} key={column}>{COLUMN_DISPLAY_NAMES[column]}</div>)}
                </div>

                {/* Table Rows */}
                {displayedProducts.map((product, index) => (
                  <div
                    key={`${product.id}-${index}`}
                    css={TABLE_ROW_STYLE}
                    onClick={() => handleAddProduct(product)}
                    title='Click to add'
                  >
                    <div css={TABLE_CELL_STYLE}>{product.orgId ? 'Yes' : 'No'}</div>
                    <div css={IMAGE_CELL_STYLE}>
                      {product.image
                        ? (
                          <img
                            css={PRODUCT_IMAGE_STYLE}
                            src={product.image}
                            alt={product.title || 'Product image'}
                          />
                        )
                        : <div css={PRODUCT_IMAGE_STYLE} />}
                    </div>
                    <div css={[TABLE_CELL_STYLE, PRICE_CELL_STYLE]}>
                      {formatUsd(product.price)}
                    </div>
                    <div css={[TABLE_CELL_STYLE, SUPPLIER_CELL_STYLE]}>
                      {getProductSearchResultSupplierCell(product) || '-'}
                    </div>

                    <div css={[TABLE_CELL_STYLE, TITLE_CELL_STYLE]}>
                      {product.title || '-'}
                    </div>
                    <div css={[TABLE_CELL_STYLE, DESCRIPTION_CELL_STYLE]}>
                      {product.description || '-'}
                    </div>
                  </div>
                ))}
              </>
            )}
          </div>
        </div>
      </div>
    </>
  );

  return createPortal(modalContent, document.body);
};
